import React, { FC, ReactNode, CSSProperties, useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import ReactDOM from 'react-dom';
import s from './Tooltip.module.css';

interface TooltipProps {
    content: ReactNode;
    children: ReactNode;
    position?: 'top' | 'bottom' | 'left' | 'right';
    offsetX?: number;
    offsetY?: number;
    arrowOffsetX?: number;
    arrowOffsetY?: number;
}

const Tooltip: FC<TooltipProps> = ({
    content,
    children,
    position = 'top',
    offsetX = 0,
    offsetY = 0,
    arrowOffsetX = 0,
    arrowOffsetY = 0,
}) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
    const [actualPosition, setActualPosition] = useState(position);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (showTooltip && wrapperRef.current && tooltipRef.current) {
            const wrapperRect = wrapperRef.current.getBoundingClientRect();
            const tooltipRect = tooltipRef.current.getBoundingClientRect();

            let top = 0;
            let left = 0;

            switch (actualPosition) {
                case 'top':
                    top = wrapperRect.top - tooltipRect.height - offsetY;
                    left =
                        wrapperRect.left + wrapperRect.width / 2 - tooltipRect.width / 2 + offsetX;
                    break;
                case 'bottom':
                    top = wrapperRect.bottom + offsetY;
                    left =
                        wrapperRect.left + wrapperRect.width / 2 - tooltipRect.width / 2 + offsetX;
                    break;
                case 'left':
                    top =
                        wrapperRect.top + wrapperRect.height / 2 - tooltipRect.height / 2 + offsetY;
                    left = wrapperRect.left - tooltipRect.width - offsetX;
                    break;
                case 'right':
                    top =
                        wrapperRect.top + wrapperRect.height / 2 - tooltipRect.height / 2 + offsetY;
                    left = wrapperRect.right + offsetX;
                    break;
            }

            const screenWidth = window.innerWidth;
            const screenHeight = window.innerHeight;

            if (top < 0) setActualPosition('bottom');
            else if (left < 0) setActualPosition('right');
            else if (top + tooltipRect.height > screenHeight) setActualPosition('top');
            else if (left + tooltipRect.width > screenWidth) setActualPosition('left');

            setCoords({
                top: top + window.scrollY,
                left: left + window.scrollX,
            });
        }
    }, [showTooltip, actualPosition, offsetX, offsetY]);

    const getArrowStyle = (): CSSProperties => {
        switch (actualPosition) {
            case 'top':
                return {
                    position: 'absolute',
                    top: '100%',
                    left: '50%',
                    transform: `translateX(calc(-50% + ${arrowOffsetX}px)) translateY(${arrowOffsetY}px)`,
                    width: 0,
                    height: 0,
                    borderLeft: '6px solid transparent',
                    borderRight: '6px solid transparent',
                    borderTop: '6px solid #fff9dd',
                };
            case 'bottom':
                return {
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: `translateX(calc(-50% + ${arrowOffsetX}px)) translateY(${arrowOffsetY}px) rotate(180deg)`,
                    width: 0,
                    height: 0,
                    borderLeft: '6px solid transparent',
                    borderRight: '6px solid transparent',
                    borderTop: '6px solid #fff9dd',
                };
            default:
                return {};
        }
    };

    return (
        <>
            <div
                ref={wrapperRef}
                className={s.tooltipWrapper}
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => {
                    setShowTooltip(false);
                    setActualPosition(position);
                }}
            >
                {children}
            </div>

            {showTooltip &&
                ReactDOM.createPortal(
                    <motion.div
                        ref={tooltipRef}
                        className={s.tooltip}
                        style={{
                            position: 'absolute',
                            top: coords.top,
                            left: coords.left,
                            zIndex: 9999,
                        }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                    >
                        {content}
                        <div style={getArrowStyle()} />
                    </motion.div>,
                    document.body,
                )}
        </>
    );
};

export default Tooltip;
